import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as EyeIcon } from '@/assets/icons/eye.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ChannelGroupResource } from '@/types/resources';
import { api } from '@/utils/api';

import ChannelsTooltip from './ChannelsTooltip';
import DQCascadeModal from './DQCascade/DQCascadeModal';
import { getCascadeGroupNumber } from './DQCascade/helpers';
import GroupForm from './GroupForm';

const Channel = styled.div`
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ChannelIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background-color: #ecbe19;
`;

interface Props {
  group: ChannelGroupResource;
}

const GroupsTableRow: FC<Props> = ({ group }) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCascadeStatus, setShowCascadeStatus] = useState(false);

  const cascadeGroupNumber = getCascadeGroupNumber(group.name);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowEditForm(true);
  };

  const onShowCascadeStatus = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowCascadeStatus(true);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        'Are you sure you want to delete this group',
        group.name,
      );

      if (!confirmed) return;

      await api.delete(`/v1/distribution/channel-groups/${group.id}`);

      queryClient.invalidateQueries(['channel_groups'], {
        exact: false,
      });

      alerts.success('Group successfully deleted', group.name);
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        <Td>
          <Text as="span" $type="smallHeadline">
            {group.name}
          </Text>
        </Td>
        <Td>
          {!!group.channels?.length ? (
            <>
              {group.channels.slice(0, 3).map((channel) => (
                <Channel key={channel.id}>
                  {!!channel.thumbnail && (
                    <ChannelIcon
                      src={channel.thumbnail}
                      alt={`${channel.name} icon`}
                    />
                  )}
                  <Text as="span" $type="smallBold">
                    {channel.name}
                  </Text>
                </Channel>
              ))}
              {group.channels.length > 3 && (
                <ChannelsTooltip channels={group.channels.slice(3)} />
              )}
            </>
          ) : (
            '-'
          )}
        </Td>
        <Td>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onEdit}>
              <PencilIcon />
              Edit
            </ActionsDropdownButton>
            {cascadeGroupNumber !== null && (
              <ActionsDropdownButton as="button" onClick={onShowCascadeStatus}>
                <EyeIcon />
                Cascade status
              </ActionsDropdownButton>
            )}
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <TrashIcon />
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </Td>
      </tr>
      <AnimatePresence>
        {!!showEditForm && (
          <GroupForm
            editableGroup={group}
            onClose={() => setShowEditForm(false)}
          />
        )}
        {showCascadeStatus && cascadeGroupNumber !== null && (
          <DQCascadeModal
            group={group}
            groupNumber={cascadeGroupNumber}
            onClose={() => setShowCascadeStatus(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GroupsTableRow;
